import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const WebRTCContext = createContext();

export const WebRTCProvider = ({ children }) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedCount, setConnectedCount] = useState(0);
  const [isError, setIsError] = useState(false);

  const [cams, setCams] = useState([]);
  const [autoConnect, setAutoConnect] = useState(false);
  const [layoutPreset, setLayoutPreset] = useState(null);

  const [scheduledJob, setScheduledJob] = useState(null);
  const [currentJob, setCurrentJob] = useState(null);

  const clearScheduledJobLocal = () => {
    setScheduledJob(null);
    setCurrentJob(null);
  };

  useEffect(() => {
    (async () => {
      await fetch(`${process.env.REACT_APP_ADDRESS}streaming/get_scheduled_job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
        .then((response) => response.json())
        .then((response) => setScheduledJob(response.job ?? null));
    })();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);

      // Clear scheduled job once it's finished to avoid stale UI display
      if (scheduledJob && scheduledJob.end !== null && now > scheduledJob.end) {
        setScheduledJob(null);
        setCurrentJob(null);
        return;
      }

      setCurrentJob((prev) => {
        const activeJob = scheduledJob && now >= scheduledJob.start && (now <= scheduledJob.end || scheduledJob.end === null) ? scheduledJob : null;

        if (prev?.start === activeJob?.start && prev?.end === activeJob?.end)
          return prev; // Prevent from re-render if no change

        return activeJob;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [scheduledJob, setScheduledJob]);

  const [steps, setSteps] = useState([
    { name: 'Connexion au système', href: '#', status: 'upcoming' },
    { name: 'Démarrage des caméras', href: '#', status: 'upcoming' },
    { name: 'Réception des flux', href: '#', status: 'upcoming' },
  ]);

  const updateStepsStatus = (status1=null, status2=null, status3=null) => {
    setSteps((prevSteps) =>
      prevSteps.map((step, i) => {
        if (i === 0 && status1 !== null) return { ...step, status: status1 }
        if (i === 1 && status2 !== null) return { ...step, status: status2 }
        if (i === 2 && status3 !== null) return { ...step, status: status3 }
        return step;
      })
    )
  };

  const videoElsRef = useRef({});
  const pcsRef = useRef({});
  const camAlertedRef = useRef({});
  const camWatchdogRef = useRef({});

  const raiseCamError = (camId, reason) => {
    if (!camId) return;
    setIsError(true);

    if (camAlertedRef.current[camId]) return;
    camAlertedRef.current[camId] = true;

    cleanupCam(camId);
  };

  const detectCameras = async (runScan=true) => {
    try {
      // Start process
      setIsConnecting(true);
      updateStepsStatus('current', 'upcoming', 'upcoming');

      // Scan request
      if (runScan) {
        let resScan;
        try {
          resScan = await fetch(`${process.env.REACT_APP_ADDRESS}streaming/scan`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          });
          if (!resScan.ok) {
            const txt = await resScan.text();
            throw new Error(`scan: HTTP ${resScan.status} - ${txt}`);
          }
        } catch (err) {
          updateStepsStatus('error', null, null);
          throw new Error("scan error");
        }
      }

      updateStepsStatus('complete', 'current', null);

      // Streams request
      await new Promise((r) => setTimeout(r, 2000));

      let resStreams;
      try {
        resStreams = await fetch(`${process.env.REACT_APP_ADDRESS}streaming/streams`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!resStreams.ok) {
          const txt = await resStreams.text();
          throw new Error(`streams: HTTP ${resStreams.status} - ${txt}`);
        }
      } catch (err) {
        updateStepsStatus(null, 'error', null);
        throw new Error("streams: network error");
      }

      // Auto connect with useEffect
      const payload = await resStreams.json();
      const streams = payload?.streams || [];

      if (streams.length > 0) {
        updateStepsStatus(null, 'complete', 'current');

        const nextCams = (streams).map((s, idx) => ({
          id: String(s.cam_id),
          label: `Cam ${idx}`,
          source: s.source,
          opened: Boolean(s.opened),
        }));

        setCams(nextCams);
        setAutoConnect(true);

        return nextCams;
      }
      else{
        updateStepsStatus(null, 'error', null);
        throw new Error("streams: no stream");
      }
    } catch (e) {
      setIsConnecting(false);
      throw e;
    }
  };

  const registerVideoEl = (camId, el) => {
    if (!camId) return;
    if (el) videoElsRef.current[camId] = el;
  };

  const cleanupCam = (camId) => {
    const pc = pcsRef.current[camId];
    if (pc) {
      try {
        pc.close();
      } catch {
        // ignore
      }
      delete pcsRef.current[camId];
    }

    const w = camWatchdogRef.current[camId];
    if (w) {
      clearInterval(w);
      delete camWatchdogRef.current[camId];
    }

    const videoEl = videoElsRef.current[camId];
    if (videoEl) videoEl.srcObject = null;

    camAlertedRef.current[camId] = false;
  };

  const disconnectCam = (camId) => {
    const pc = pcsRef.current[camId];
    if (pc) {
      pc.close();
      delete pcsRef.current[camId];
    }

    const w = camWatchdogRef.current[camId];
    if (w) {
      clearInterval(w);
      delete camWatchdogRef.current[camId];
    }

    const videoEl = videoElsRef.current[camId];
    if (videoEl) videoEl.srcObject = null;

    camAlertedRef.current[camId] = false;
  };

  const disconnectAll = () => {
    Object.keys(pcsRef.current).forEach((camId) => disconnectCam(camId));
  };

  const connectCam = async (camId) => {
    if (pcsRef.current[camId]) return;

    camAlertedRef.current[camId] = false;

    const videoEl = videoElsRef.current[camId];

    try {
      const pc = new RTCPeerConnection();
      pcsRef.current[camId] = pc;

      pc.addTransceiver("video", { direction: "recvonly" });

      pc.ontrack = (e) => {
        videoEl.srcObject = e.streams[0];

        const track = e.track;

        if (track) {
          track.onended = () => raiseCamError(camId, "track ended");
          track.onmute = () => raiseCamError(camId, "track muted");
        }

        // Watchdog: detect frozen stream (server crash, network drop, etc.)
        if (camWatchdogRef.current[camId]) {
          clearInterval(camWatchdogRef.current[camId]);
          delete camWatchdogRef.current[camId];
        }

        let lastBytes = null;
        let lastChangeTs = Date.now();

        camWatchdogRef.current[camId] = setInterval(async () => {
          const currentPc = pcsRef.current[camId];
          if (!currentPc) return;

          try {
            const stats = await currentPc.getStats();
            let bytes = null;

            stats.forEach((r) => {
              if (r.type === "inbound-rtp" && r.kind === "video") {
                if (typeof r.bytesReceived === "number") bytes = r.bytesReceived;
              }
            });

            if (bytes === null) return;

            if (lastBytes === null) {
              lastBytes = bytes;
              lastChangeTs = Date.now();
              return;
            }

            if (bytes > lastBytes) {
              lastBytes = bytes;
              lastChangeTs = Date.now();
              return;
            }

            // No bytes increase -> frozen
            const frozenMs = Date.now() - lastChangeTs;
            if (frozenMs > 3000) {
              raiseCamError(camId, "stream frozen (no video bytes)");
            }
          } catch {
            // ignore transient errors
          }
        }, 1000);

        const p = videoEl.play?.();
        if (p && typeof p.catch === "function") {
          p.catch(() => {
            // Autoplay can still be blocked in some cases; ignore here
          });
        }
      };

      pc.onconnectionstatechange = () => {
        console.log(`cam ${camId} state:`, pc.connectionState);

        if (pc.connectionState === "failed") raiseCamError(camId, "pc failed");
        if (pc.connectionState === "disconnected") raiseCamError(camId, "pc disconnected");
        if (pc.connectionState === "closed") raiseCamError(camId, "pc closed");
      };

      pc.oniceconnectionstatechange = () => {
        console.log(`cam ${camId} ice:`, pc.iceConnectionState);

        if (pc.iceConnectionState === "failed") raiseCamError(camId, "ice failed");
        if (pc.iceConnectionState === "disconnected") raiseCamError(camId, "ice disconnected");
        if (pc.iceConnectionState === "closed") raiseCamError(camId, "ice closed");
      };

      pc.onicegatheringstatechange = () => {
        console.log(`cam ${camId} gathering:`, pc.iceGatheringState);
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const res = await fetch(
        `${process.env.REACT_APP_ADDRESS}streaming/watch/${encodeURIComponent(camId)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sdp: pc.localDescription?.sdp,
            type: pc.localDescription?.type,
          }),
        }
      );

      if (!res.ok) {
        const txt = await res.text();
        cleanupCam(camId);
        throw new Error(`cam ${camId}: HTTP ${res.status} - ${txt}`);
      }

      const answer = await res.json();
      await pc.setRemoteDescription(answer);
    } catch (e) {
      cleanupCam(camId);
      throw e;
    }
  };

  const connectAll = useCallback(async () => {
    try {
      setIsConnecting(true);

      const results = await Promise.allSettled(cams.map((c) => connectCam(c.id)));

      const okCount = results.filter((r) => r.status === "fulfilled").length;
      const failures = results
        .map((r, idx) => ({ r, camId: cams[idx]?.id }))
        .filter((x) => x.r.status === "rejected");

      if (failures.length) {
        console.warn(
          "Cam failures:",
          failures.map((f) => ({ cam: f.camId, err: String(f.r.reason) }))
        );
        updateStepsStatus(null, null, 'error');
      } else {
        updateStepsStatus(null, null, 'complete');
      }

      setConnectedCount(okCount);

      if (okCount > 0) setIsConnected(true);
      else setIsConnected(false);

    } catch (e) {
      console.error(e);
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
    }
  }, [cams]);

  const stopAll = () => {
    disconnectAll();
    setIsConnected(false);
    setConnectedCount(0);
  };

  // useEffect needed to reflect changes on cams (not possible directly in detectCameras)
  useEffect(() => {
    if (!autoConnect) return;
    if (!cams.length) {
      setAutoConnect(false);
      return;
    }
    setAutoConnect(false);
    connectAll().catch(() => {});
  }, [autoConnect, cams, connectAll]);

  useEffect(() => {
    return () => {
      disconnectAll();
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      cams,
      isConnecting,
      isConnected,
      connectedCount,
      isError,
      layoutPreset,
      setLayoutPreset,
      registerVideoEl,
      connectAll,
      stopAll,
      connectCam,
      disconnectCam,
      cleanupCam,
      disconnectAll,
      detectCameras,
      steps,
      scheduledJob,
      setScheduledJob,
      clearScheduledJobLocal,
      currentJob,
    }),
    [cams, isConnecting, isConnected, connectedCount, isError, layoutPreset, steps, scheduledJob, currentJob]
  );

  return <WebRTCContext.Provider value={contextValue}>{children}</WebRTCContext.Provider>;
};

export const useWebRTC = () => {
  return useContext(WebRTCContext);
};
